// Packages
import {markRaw} from 'vue';

// Services and helpers
import {startDrawing, stopDrawing, clearDrawings, undoLine} from '@/lib/toolbox/drawMode.js';
import {enterCircleMode, exitCircleMode, clearCircle} from '@/lib/toolbox/circleMode';
import {createNewBlockPlan} from '@/services/GraphicService/BlockPlanGraphic/createBlockPlanGraphic';
import {createNewPoly} from '@/services/GraphicService/PolyGraphic/createNewPolyGraphic';
import {blue} from '@/lib/colors';

// Components
import RadiusBox from '@/components/Toolbox/sections/RadiusBox.vue';
import ScribbleBox from '@/components/Toolbox/sections/ScribbleBox.vue';
import DrawModeIcon from '@/components/svgs/ToolboxIcons/DrawModeIcon.vue';
import RadiusIcon from '@/components/svgs/RadiusIcon.vue';
import BlockPlanIcon from '@/components/svgs/ToolboxIcons/BlockPlanIcon.vue';
import BlockPlanDrawer from '@/components/Toolbox/sections/BlockPlanDrawer.vue';
import SquareFootIcon from '@/components/svgs/ToolboxIcons/SquareFootIcon.vue';

/**
 * @typedef {Object} State
 * @property {Function} toolboxCancelCallback
 * @property {ToolboxDrawer[]} toolboxDrawers
 * @property {ToolboxOverlay} toolboxOverlay
 *
 * @typedef {import('@/services/OverlaysService/Overlay.js').Overlay} ToolboxOverlay
 *
 * @typedef {Object} ToolboxTool
 * @property {string} name
 * @property {string} id
 * @property {import('vue').Component} component
 * @property {Function} onSelect
 * @property {Function} onDeselect
 * @property {number} [minZoom]
 * @property {Function} [onClear]
 * @property {boolean} [handleAsFeature]
 * @property {Object[]} [buttons]
 *
 * @typedef {Object} ToolboxDrawer
 * @property {string} name
 * @property {string} id
 * @property {import('vue').Component} icon
 * @property {number} [minZoom]
 * @property {import('vue').Component} [drawerComponent]
 * @property {ToolboxTool[]} tools
 */

/** @type {State} */
const state = {
  toolboxOverlay: null,
  toolboxCancelCallback: null,
  toolboxDrawers: [
    {
      name: 'Scribble',
      id: 'drawer-scribble',
      icon: markRaw(DrawModeIcon),
      tools: [{
        id: 'tool-scribble',
        component: markRaw(ScribbleBox),
        onSelect: startDrawing,
        onDeselect: stopDrawing,
        onClear: clearDrawings,
        buttons: [{
          name: 'Undo',
          borderColor: '$npGray',
          fontColor: '$npGray',
          fill: 'white',
          handler: undoLine,
        }],
      }],
    },
    {
      name: 'Measure',
      id: 'drawer-poly',
      icon: markRaw(SquareFootIcon),
      tools: [{
        id: 'tool-poly',
        handleAsFeature: true,
        onSelect: (onSelectCallback) => state.toolboxCancelCallback = createNewPoly(onSelectCallback, {
          strokeColor: blue,
          fillColor: blue,
          calculateSF: true,
        }),
        onDeselect: () => state.toolboxCancelCallback?.(),
      }],
    },
    {
      name: 'Radius',
      id: 'drawer-radius',
      icon: markRaw(RadiusIcon),
      tools: [{
        id: 'tool-radius',
        component: markRaw(RadiusBox),
        onSelect: enterCircleMode,
        onDeselect: exitCircleMode,
        onClear: clearCircle,
      }],
    },
    {
      name: 'BlockPlan',
      id: 'drawer-blockPlan',
      icon: markRaw(BlockPlanIcon),
      minZoom: 13,
      drawerComponent: markRaw(BlockPlanDrawer),
      tools: [
        {
          id: 'tool-blockPlan-crossDock',
          handleAsFeature: true,
          minZoom: 13,
          onSelect: (onSelectCallback) => createNewBlockPlan('crossDock', onSelectCallback),
        },
        {
          id: 'tool-blockPlan-singleLoad',
          handleAsFeature: true,
          minZoom: 13,
          onSelect: (onSelectCallback) => createNewBlockPlan('singleLoad', onSelectCallback),
        },
      ],
    },
  ],
};

const mutations = {
  /**
   * @param {State} state
   * @param {ToolboxOverlay} overlay
   */
  setToolboxOverlay: (state, overlay) => {
    state.toolboxOverlay = overlay;
  },

  /**
   * @param {State} state
   * @param {Object} payload
   * @param {string} payload.drawerId
   * @param {string} payload.toolId
   * @param {string} payload.buttonId
   * @param {Object} payload.updatedBtnState
   */
  updateButtonState: (state, {drawerId, toolId, buttonId, updatedBtnState}) => {
    const drawer = state.toolboxDrawers.find(({id}) => id === drawerId);
    if (!drawer) return;

    const tool = drawer.tools.find(({id}) => id === toolId);
    if (!tool) return;

    const button = tool.buttons?.find(({id}) => id === buttonId);
    if (!button) return;

    for (const key in updatedBtnState) {
      if (Object.hasOwn(button, key)) {
        button[key] = updatedBtnState[key];
      }
    }
  },
};

const storeConfig = {
  namespaced: true,
  state: () => state,
  mutations,
};

export default storeConfig;
